import { Models } from "@hex-insights/app-modules";
import { Button, Row } from "@hex-insights/core";
import {
	AddSubFormButtonRenderProps,
	DateTimeField,
	FileUploadField,
	Form,
	RadioField,
	SubFormField,
	SubFormRenderProps,
	TextAreaInput,
	TextField,
	TextFieldInputProps,
} from "@hex-insights/forms";
import { Icon } from "../../../Components/Icon";
import { SelectTextInput } from "../../../Components/SelectTextInput";
import {
	EnrollmentApplicationStudentFormValues,
	EnrollmentApplicationStudentSchoolFormValues,
} from "../../FormUtilities";
import { NameOrderField } from "../_General";
import { AcademicYearSelectField } from "../AcademicYear";
import { ClassYearSelectField } from "../ClassYear";
import { CountrySelectField } from "../Country";
import { EnrollmentApplicationSelectField } from "../EnrollmentApplication";
import { EnrollmentApplicationStudentSchoolForm } from "../EnrollmentApplicationStudentSchool";
import { LanguageSelectField } from "../Language";
import { BaseFieldProps } from "../shared";
import { StudentSelectField } from "../Student";
import styles from "./field.module.css";

/**
 * Generic props for fields of the EnrollmentApplicationStudent model.
 */
type FieldProps<
	K extends keyof EnrollmentApplicationStudentFormValues.Base = keyof EnrollmentApplicationStudentFormValues.Base,
> = BaseFieldProps<Pick<EnrollmentApplicationStudentFormValues.Base, K>>;

/**
 * Generic props for fields of the EnrollmentApplicationStudent model that only appear in the create form.
 */
type CreateFieldProps<
	K extends keyof EnrollmentApplicationStudentFormValues.Create = keyof EnrollmentApplicationStudentFormValues.Create,
> = BaseFieldProps<Pick<EnrollmentApplicationStudentFormValues.Create, K>>;

/**
 * Generic props for fields of the EnrollmentApplicationStudent model that only appear in the detail form.
 */
type DetailFieldProps<
	K extends keyof EnrollmentApplicationStudentFormValues.Detail = keyof EnrollmentApplicationStudentFormValues.Detail,
> = BaseFieldProps<Pick<EnrollmentApplicationStudentFormValues.Detail, K>>;

/**
 * Field component for the `image` field of the EnrollmentApplicationStudent model.
 */
export function Image({ formState, url, ...props }: FieldProps<"imageKey" | "imageContentType"> & { url?: string }) {
	Models.requireURLForUpdateForm(url, false, formState.formType, "Image");

	return (
		<FileUploadField
			formState={formState}
			keyFieldName="imageKey"
			contentTypeFieldName="imageContentType"
			url={url}
			optional
			{...props}
		/>
	);
}

/**
 * Field component for the `name` field of the EnrollmentApplicationStudent model.
 */
export function Name({ formState, ...props }: DetailFieldProps<"name">) {
	return <TextField formState={formState} name="name" {...props} />;
}

/**
 * Field component for the `givenName` field of the EnrollmentApplicationStudent model.
 */
export function GivenName({ formState, ...props }: FieldProps<"givenName">) {
	return <TextField formState={formState} name="givenName" {...props} />;
}

/**
 * Field component for the `middleName` field of the EnrollmentApplicationStudent model.
 */
export function MiddleName({ formState, ...props }: FieldProps<"middleName">) {
	return <TextField formState={formState} name="middleName" optional {...props} />;
}

/**
 * Field component for the `familyName` field of the EnrollmentApplicationStudent model.
 */
export function FamilyName({ formState, ...props }: FieldProps<"familyName">) {
	return <TextField formState={formState} name="familyName" {...props} />;
}

/**
 * Field component for the `nameOrder` field of the EnrollmentApplicationStudent model.
 */
export function NameOrder(props: FieldProps<"nameOrder" | "givenName" | "familyName">) {
	const { givenName, familyName } = props.formState.formValues;

	return <NameOrderField {...props} name="nameOrder" givenName={givenName} familyName={familyName} nickname="" />;
}

/**
 * Field component for the `dateOfBirth` field of the EnrollmentApplicationStudent model.
 */
export function DateOfBirth({ formState, ...props }: FieldProps<"dateOfBirth">) {
	return <DateTimeField formState={formState} name="dateOfBirth" precision="day" {...props} />;
}

/**
 * Field component for the `gender` field of the EnrollmentApplicationStudent model.
 */
export function Gender({ formState, ...props }: FieldProps<"gender">) {
	return <TextField formState={formState} name="gender" optional Input={GenderInput} {...props} />;
}

const genderOptions = [{ value: "Male" }, { value: "Female" }];

function GenderInput(props: TextFieldInputProps) {
	return (
		<SelectTextInput
			options={genderOptions}
			{...props}
			showInvalid={props.className?.includes("invalid") ?? false}
			className={undefined}
		/>
	);
}

/**
 * Field component for the `englishLanguageFluency` field of the EnrollmentApplicationStudent model.
 */
export function EnglishLanguageFluency({ formState, ...props }: FieldProps<"englishLanguageFluency">) {
	return (
		<RadioField
			formState={formState}
			name="englishLanguageFluency"
			options={EnrollmentApplicationStudentFormValues.englishLanguageFluencyOptions}
			blankValue={null}
			{...props}
		/>
	);
}

/**
 * Field component for the `questionnaireActivities` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireActivities({ formState, ...props }: FieldProps<"questionnaireActivities">) {
	return <TextField formState={formState} name="questionnaireActivities" Input={QuestionaireTextInput} {...props} />;
}

const questionnaireBooleanOptions = [
	{ value: true, label: "Yes" },
	{ value: false, label: "No" },
];

/**
 * Field component for the `questionnaireHasPsychTreatment` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireHasPsychTreatment({ formState, ...props }: FieldProps<"questionnaireHasPsychTreatment">) {
	return (
		<RadioField
			formState={formState}
			name="questionnaireHasPsychTreatment"
			options={questionnaireBooleanOptions}
			{...props}
		/>
	);
}

/**
 * Field component for the `questionnairePsychTreatmentDescription` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnairePsychTreatmentDescription({
	formState,
	...props
}: FieldProps<"questionnairePsychTreatmentDescription" | "questionnaireHasPsychTreatment">) {
	return (
		<TextField
			formState={formState}
			name="questionnairePsychTreatmentDescription"
			optional={!formState.formValues.questionnaireHasPsychTreatment}
			Input={QuestionaireTextInput}
			{...props}
		/>
	);
}

/**
 * Field component for the `questionnaireHasSpecialNeeds` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireHasSpecialNeeds({ formState, ...props }: FieldProps<"questionnaireHasSpecialNeeds">) {
	return (
		<RadioField
			formState={formState}
			name="questionnaireHasSpecialNeeds"
			options={questionnaireBooleanOptions}
			{...props}
		/>
	);
}

/**
 * Field component for the `questionnaireSpecialNeedsDescription` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireSpecialNeedsDescription({
	formState,
	...props
}: FieldProps<"questionnaireSpecialNeedsDescription" | "questionnaireHasSpecialNeeds">) {
	return (
		<TextField
			formState={formState}
			name="questionnaireSpecialNeedsDescription"
			optional={!formState.formValues.questionnaireHasSpecialNeeds}
			Input={QuestionaireTextInput}
			{...props}
		/>
	);
}

/**
 * Field component for the `questionnaireHasBehaviors` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireHasBehaviors({ formState, ...props }: FieldProps<"questionnaireHasBehaviors">) {
	return (
		<RadioField
			formState={formState}
			name="questionnaireHasBehaviors"
			options={questionnaireBooleanOptions}
			{...props}
		/>
	);
}

/**
 * Field component for the `questionnaireBehaviorsDescription` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireBehaviorsDescription({
	formState,
	...props
}: FieldProps<"questionnaireBehaviorsDescription" | "questionnaireHasBehaviors">) {
	return (
		<TextField
			formState={formState}
			name="questionnaireBehaviorsDescription"
			optional={!formState.formValues.questionnaireHasBehaviors}
			Input={QuestionaireTextInput}
			{...props}
		/>
	);
}

/**
 * Field component for the `questionnaireDescription` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireDescription({ formState, ...props }: FieldProps<"questionnaireDescription">) {
	return <TextField formState={formState} name="questionnaireDescription" Input={QuestionaireTextInput} {...props} />;
}

/**
 * Field component for the `questionnaireAdditionalInformation` field of the EnrollmentApplicationStudent model.
 */
export function QuestionnaireAdditionalInformation({
	formState,
	...props
}: FieldProps<"questionnaireAdditionalInformation">) {
	return (
		<TextField
			formState={formState}
			name="questionnaireAdditionalInformation"
			optional
			Input={QuestionaireTextInput}
			{...props}
		/>
	);
}

function QuestionaireTextInput(props: TextFieldInputProps) {
	return <TextAreaInput {...props} minRows={3} />;
}

export namespace AcademicYear {
	export type SelectProps = FieldProps<"academicYearID"> &
		Omit<
			AcademicYearSelectField.SelectProps<EnrollmentApplicationStudentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `academicYear` edge of the EnrollmentApplicationStudent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <AcademicYearSelectField.Select formState={formState} name="academicYearID" {...props} />;
	}
}

export namespace ClassYear {
	export type SelectProps = FieldProps<"classYearID"> &
		Omit<
			ClassYearSelectField.SelectProps<EnrollmentApplicationStudentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `classYear` edge of the EnrollmentApplicationStudent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <ClassYearSelectField.Select formState={formState} name="classYearID" {...props} />;
	}
}

export namespace CurrentClassYear {
	export type SelectProps = FieldProps<"currentClassYearID"> &
		Omit<
			ClassYearSelectField.SelectProps<EnrollmentApplicationStudentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `currentClassYear` edge of the EnrollmentApplicationStudent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <ClassYearSelectField.Select formState={formState} name="currentClassYearID" optional {...props} />;
	}
}

export namespace EnrollmentApplication {
	export type SelectProps = FieldProps<"enrollmentApplicationID"> &
		Omit<
			EnrollmentApplicationSelectField.SelectProps<EnrollmentApplicationStudentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `enrollmentApplication` edge of the EnrollmentApplicationStudent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <EnrollmentApplicationSelectField.Select formState={formState} name="enrollmentApplicationID" {...props} />;
	}
}

export namespace EnrollmentApplicationStudentSchools {
	/**
	 * SubForm field component for the `enrollmentApplicationStudentSchools` edge of the EnrollmentApplicationStudent model.
	 */
	export function SubForm({ formState, ...props }: CreateFieldProps<"enrollmentApplicationStudentSchools">) {
		return (
			<SubFormField
				formState={formState}
				name="enrollmentApplicationStudentSchools"
				blankItem={EnrollmentApplicationStudentSchoolFormValues.blankItemInEnrollmentApplicationStudent}
				{...props}
			>
				{SubForm.SubForm}
				{SubForm.AddButton}
			</SubFormField>
		);
	}

	SubForm.SubForm = function ({
		formState,
		onRemoveClick,
	}: SubFormRenderProps<EnrollmentApplicationStudentSchoolFormValues.CreateInEnrollmentApplicationStudent>) {
		return (
			<Form.Column className={styles["enrollment-application-student-schools__item"]}>
				<div className={styles["enrollment-application-student-schools__item__remove-button-container"]}>
					<Button
						variant="link"
						size="small"
						onClick={onRemoveClick}
						disabled={formState.disabled}
						className="button--icon"
					>
						<Icon.X size={16} className="block" />
					</Button>
				</div>
				<EnrollmentApplicationStudentSchoolForm.CreateFieldsInEnrollmentApplicationStudent formState={formState} />
			</Form.Column>
		);
	};

	SubForm.AddButton = function ({
		onClick,
		disabled,
		numItems,
	}: AddSubFormButtonRenderProps<
		Pick<EnrollmentApplicationStudentFormValues.Create, "enrollmentApplicationStudentSchools">,
		EnrollmentApplicationStudentSchoolFormValues.CreateInEnrollmentApplicationStudent
	>) {
		return (
			<Button variant="link" size="small" onClick={onClick} disabled={disabled}>
				<Row justify="spaced-start" align="center" horizontalSpacing="0.25rem">
					<Icon.Plus size={16} />
					<span>{numItems === 0 ? "Add School" : "Add Another School"}</span>
				</Row>
			</Button>
		);
	};
}

export namespace Languages {
	export type SelectProps = FieldProps<"languageIDs"> &
		Omit<
			LanguageSelectField.MultiSelectProps<EnrollmentApplicationStudentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `languages` edge of the EnrollmentApplicationStudent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <LanguageSelectField.MultiSelect formState={formState} name="languageIDs" {...props} />;
	}
}

export namespace Nationalities {
	export type SelectProps = FieldProps<"nationalityIDs"> &
		Omit<
			CountrySelectField.MultiSelectProps<EnrollmentApplicationStudentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `nationalities` edge of the EnrollmentApplicationStudent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <CountrySelectField.MultiSelect formState={formState} name="nationalityIDs" {...props} />;
	}
}

export namespace Student {
	export type SelectProps = FieldProps<"studentID"> &
		Omit<
			StudentSelectField.SelectProps<EnrollmentApplicationStudentFormValues.Base>,
			"formState" | "name" | "optional"
		>;

	/**
	 * Field component for the `student` edge of the EnrollmentApplicationStudent model.
	 */
	export function Select({ formState, ...props }: SelectProps) {
		return <StudentSelectField.Select formState={formState} name="studentID" optional {...props} />;
	}
}
